import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Typography,
  Link,
} from "@mui/material";
import AccordionFAQ from "../../components/layout/AccordionFAQ";
import BulletList from "../../components/layout/BulletList";
import { useTheme } from "@mui/material/styles";

const faqItems = [
  {
    question: "Can I bid an item that I've listed?",
    answer: (<>
      You may bid with items you've listed if you do not have a spare if you have received no bids by the last 5 minutes of the bidding period.
    </>),
  },
  {
    question: "Can I take down my listing/bid?",
    answer: (<>
      You may alter/remove a listing during the listing period, and you may alter/remove a bid during the bidding period. If you are altering a bid, please make sure to repost the entire bid so that your host can easily copy/paste and keep track of your new offer. Please be mindful of how many times you edit your bid!
    </>),
  },
  {
    question: "What happens if a lister/bidder poofs during a shuffle?",
    answer: (<>
       Once most users have accepted/passed bids, the host will reach out to any lister who has not appeared on the board to accept/pass their bid. The host will ask the user to either post on the board, or neomail them with their chosen bid so the page can be updated, and if they are accepting the bid, to reach out to the user whose bid they are accepting. If a winning bidder has poofed, either the host or the lister may reach out to them about the shuffle. If you are a lister/bidder and might not be around for the end of the shuffle, please state so in your listing/bids so the host & other listers can keep track! 
    </>),
  },
  {
    question: "What do I do if I feel that a bidder is acting in bad faith?",
    answer: (<>
      If you believe that a bidder is acting in bad faith, please contact the host by neomail with your concerns. The host may decide whether or not they will consider the bids valid. If a user has had several infractions, they may be blacklisted from joining in future shuffles.   
    </>),
  },
  {
    question: "How do I find the image URL for my item?",
    answer: (<>
      On a desktop computer, navigate to the DTI or JN listing for your item. Right click on the image and click "copy image link." Now you can paste it into your message.
      <br />
      On mobile, navigate to the DTI or JN listing for your item. Long press on the image of your item and open it in a new tab. Then click on the URL bar and copy the link. Now you can paste it into your message. 
    </>),
  },
];

const sections = [
  { id: "intro", title: "Intro to NC Shuffles" },
  {
    id: "types",
    title: "Types of Shuffles",
    children: [
      { id: "normal", title: "Normal Shuffle" },
      { id: "free-pass", title: "Free Pass Shuffle" },
      { id: "no-pass", title: "No Pass Shuffle" },
      { id: "hnh", title: "Half & Half Shuffle" }
    ]
  },
  { id: "guidelines", title: "General Rules & Guidelines"},
  { id: "faq", title: "Frequently Asked Questions" },
];

const ShuffleRules = () => {
  const theme = useTheme();

  return (
    <GuideLayout title="shuffle" sections={sections}>
      <Section id="intro" title="Intro to NC Shuffles">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: August 2025. Also hosted on <Link sx={{ color: theme.palette.secondary.main }} href="https://neopets.com/~yyfa">neopets.com/~yyfa</Link>.
        </Typography>
        <Typography paragraph>
          An NC Shuffle is a trading game! The purpose of the game is to be introduced to new items that you may not have considered before, and for this reason, tradelists and wishlists should not be posted on the shuffle boards.
        </Typography>
        <Typography paragraph>
          Typically shuffles are done over two different boards. The first board is for users to post items they want to list. This is called the listing period. Valid listings will then be added into the pot, which is a petpage run by the shuffle's host(s) (the person(s) running the board). The pot will have the lister's username, item, value and item image for each listing.
        </Typography>
        <Typography paragraph>
          The second board is for bidding and acceptances. Bidders will post their offers on the board. The host(s) may or may not track valid bids on the pot page, though listers should track their own bids anyway. Once the bidding period ends, listers may pass or accept (depending on the rules of the shuffle) on the bids that they received. If a bid is accepted, you make the trade!
        </Typography>
        <Typography paragraph>
          New to NC and had no idea what any of this meant? Check out our <Link href="/guides/beginner-nc-guide">Beginner's NC guide.</Link>
        </Typography>
      </Section>

      <Section id="types" title="Types of Shuffles">
        <Subheader id="normal" title="Normal Shuffle" />
          <Typography paragraph>
          In a Normal Neocash Shuffle, listers may pass on their bids if they receive less than two bids. If a lister receives two or more bids on their item, they must accept one of those bids and trade their item.
          </Typography>
          <Typography paragraph>
          The hosts cannot force you to accept a bid, however it is in the spirit of the game to accept, even if you do not prefer any of your bids versus the item you have listed. With this in mind, it is always best to list items you are not overly attached and wouldn't be disappointed if you have to trade them away, or stick to bidding where you can control what you offer on.
          </Typography>

        <Subheader id="free-pass" title="Free Pass Shuffle" />
          <Typography paragraph>
          In a Free Pass Neocash Shuffle, listers may pass on all of their bids, no matter how many they receive.
          </Typography>
          <Typography paragraph>
          Typically hosts will impose a 1-2 item limit per lister in free-pass shuffles in order to keep up with the listings and bids, as it can get overwhelming without these limits.
          </Typography>
          <Typography paragraph>
          Free passes typically have a large pot with HTF or high value items, and you may pass on all bids if you're unsatisfied. However, it is common for a lot of bids to be passed on in a free pass shuffle, so trades are less commonly made in a free pass.
          </Typography>

        <Subheader id="no-pass" title="No Pass Shuffle" />
          <Typography paragraph>
          In a No Pass Neocash Shuffle, listers cannot pass on their items - they must accept a bid unless their item has received zero bids.
          </Typography>
          <Typography paragraph>
          The hosts cannot force you to accept a bid. However, it is in the spirit of the game to accept, even if you do not prefer any of your bids versus the item you have listed. With this in mind, it is always best to list items you are not overly attached and wouldn't be disappointed if you have to trade them away, or stick to bidding where you can control what you offer on.
          </Typography>

        <Subheader id="hnh" title="Half & Half Shuffle" />
          <Typography paragraph>
            In a H&H (Half & Half) Neocash Shuffle, listers must post two items to trade. If only one of your items is bid on, you may pass. If both items receive valid bids, you must accept at least one bid. If a lister chooses to post only one item, any valid bid is forcing.
          </Typography>
          <Typography paragraph>
              The hosts cannot force you to accept a bid. However, it is in the spirit of the game to accept, even if you do not prefer any of your bids versus the item you have listed. With this in mind, it is always best to list items you are not overly attached and wouldn't be disappointed if you have to trade them away, or stick to bidding where you can control what you offer on.
          </Typography>
      </Section>

      <Section id="guidelines" title="General Rules & Guidelines">
        <Typography paragraph>
          The following rules apply to all NC Shuffles, regardless of the type of shuffle.
        </Typography>

        <Subheader id="listing" title="Listing" />
        <Typography paragraph>
          During listing, please follow this format:
          <br /><br />
          <strong>
          @yourusername - ITEM NAME (your value)
          <br />  
          [IMAGE URL OF YOUR ITEM]
          <br />
          Notes: (any notes you have for your listing)
          </strong>
        </Typography>
        <Typography paragraph>
          If you have any notes for your listing (i.e. 1 item only) please include that with your intial listing. Additionally, if you're fine with gbcs, caps, cookies being a part of the bid on your item, please say so in your notes.
        </Typography>

        <Subheader id="bidding" title="Bidding" />
        <Typography paragraph>
          If you wish to bid on an item, please use this format:
          <br /><br />
          <strong>
          @username of lister - ITEM THEY LISTED (their value)
          <br />
          ITEM YOU ARE OFFERING (your value) @yourusername
          </strong>
        </Typography>
        <Typography paragraph>
          When bidding, your bid must come close to or exceed the value of the item that you are bidding on, or your bid will not be considered forcing. Please keep in mind DTI offering/seeking ratios as well as personal values when it comes to matching or exceeding values.
        </Typography>

        <Subheader id="disallowed" title="Disallowed Items" />
        <Typography paragraph>
          <BulletList items={[
            "Common 2:1 or 1 value items (such as Bronze Clockwork Wings)",
            "Pet style tokens **",
            "Buyable items (CUSTOM may be offered in bids if the lister allows)",
            "D/SSS (listing only- these items may be offered in bids if the lister allows)",
            "(BF)GBC/caps/etc. (listing only- these items may be offered in bids if the lister allows)",
          ]} />
           <strong>**</strong> = May be allowed in listing per host's rules
        </Typography>
        
        <Subheader id="additional" title="Additional Guidelines" />
        <Typography paragraph>
          <BulletList items={[
            "Do not include your tradelist as part of your bid.",
            "You can bid the same item(s) on multiple listings, however, if more than one lister chooses the same bid, the bidder can decide which trade to take.",
            "Once bidding has ended, please wait until the host(s) have indicated that the pot page is finalized & up to date before accepting any bids or passing.",
            "GBCs/cookies/SSS/DSSS/custom are not valid bids unless specifically stated by the lister that they want it."
          ]} />
        </Typography>
        <Typography paragraph>
          Please make sure that you're following the listing/bidding formats indicated above, INCLUDING the image url of your item when listing. Failure to follow the format or failure to indicate your personal values can be very time-consuming for a host to correct & could lead to your listing/bid not being included!
        </Typography>
        <Typography paragraph>
          Please pay special attention to the opening posts of each shuffle and the pot page of your host. Sometimes hosts may include an item listing limit or additional rules/restrictions on what items can be listed or bid.
        </Typography>
      </Section>

            <Section id="faq">
        <AccordionFAQ subheader={
          <Typography
            variant="h4"
            gutterBottom
            sx={theme => ({
              color: theme.palette.accent.main
            })}>
Frequently Asked Questions
          </Typography>}
          items={faqItems} />
      </Section>

    </GuideLayout>
  );
};

export default ShuffleRules;
